import { useState } from 'react';
import CodeCard from './CodeCard';
import CodeRequestModal from '../common/CodeRequestModal';
import { CodeSearchResult } from '../../types/code.types';

interface SearchResultsProps {
    results: CodeSearchResult[];
    loading: boolean;
    totalCount: number;
    searchTerm?: string;
}

const SkeletonRow = () => (
    <div className="bg-white border border-gray-100 rounded-xl px-4 py-3.5 flex items-center gap-4 animate-pulse">
        <div className="w-1.5 h-1.5 rounded-full bg-gray-100 flex-shrink-0" />
        <div className="w-20 h-7 bg-gray-100 rounded-lg flex-shrink-0" />
        <div className="flex-1 space-y-2">
            <div className="h-3.5 bg-gray-100 rounded-full" style={{ width: '72%' }} />
            <div className="h-2.5 bg-gray-50 rounded-full" style={{ width: '48%' }} />
        </div>
        <div className="w-16 h-5 bg-gray-50 rounded-full hidden lg:block" />
        <div className="w-16 h-2 bg-gray-50 rounded-full hidden xl:block" />
    </div>
);

const SearchResults = ({ results, loading, totalCount, searchTerm = '' }: SearchResultsProps) => {
    const [modalOpen, setModalOpen] = useState(false);

    if (loading) {
        return (
            <div className="space-y-2">
                {Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)}
            </div>
        );
    }

    if (!searchTerm) {
        return (
            <div className="flex flex-col items-center justify-center py-24 text-center">
                <div className="w-16 h-16 bg-gray-50 border border-gray-100 rounded-2xl flex items-center justify-center mb-4">
                    <svg className="text-gray-300" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                    </svg>
                </div>
                <p className="text-sm font-bold text-gray-500 mb-1">Search medical codes</p>
                <p className="text-xs text-gray-400">Enter a diagnosis, condition, drug name, or code</p>
            </div>
        );
    }

    if (results.length === 0) {
        return (
            <>
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 bg-gray-50 border border-gray-100 rounded-2xl flex items-center justify-center mb-4">
                        <svg className="text-gray-300" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                        </svg>
                    </div>
                    <p className="text-sm font-bold text-gray-700 mb-1">No results for "{searchTerm}"</p>
                    <p className="text-xs text-gray-400 mb-8 max-w-xs leading-relaxed">
                        Try different keywords, or let our team research and email you the exact code — free.
                    </p>
                    <div className="bg-white border border-teal-100 rounded-2xl p-6 max-w-sm w-full shadow-sm">
                        <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                            <svg className="text-teal-600" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
                            </svg>
                        </div>
                        <h3 className="text-sm font-bold text-gray-800 mb-1.5">Request This Code — Free</h3>
                        <p className="text-[11px] text-gray-500 mb-4 leading-relaxed">
                            Our medical coding experts will research and email you the exact code within 3-4 business days.
                        </p>
                        <button
                            onClick={() => setModalOpen(true)}
                            className="w-full bg-teal-600 text-white text-[12px] font-bold px-5 py-2.5 rounded-xl hover:bg-teal-700 transition-all"
                        >
                            Request Code — Free
                        </button>
                        <p className="text-[9px] text-gray-400 mt-2.5 text-center">Trusted by 1000+ medical billers & coders</p>
                    </div>
                </div>
                <CodeRequestModal isOpen={modalOpen} onClose={() => setModalOpen(false)} searchTerm={searchTerm} />
            </>
        );
    }

    return (
        <>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <p className="text-[12px] font-bold text-gray-700">Results</p>
                    <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full font-mono">
                        {totalCount.toLocaleString()}
                    </span>
                </div>
                <button
                    onClick={() => setModalOpen(true)}
                    className="flex items-center gap-1.5 text-[11px] text-teal-600 hover:underline font-semibold"
                >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
                    </svg>
                    Need a specific code?
                </button>
            </div>
            <div className="space-y-2">
                {results.map((code, i) => (
                    <CodeCard key={`${code.code}-${i}`} code={code} />
                ))}
            </div>
            <CodeRequestModal isOpen={modalOpen} onClose={() => setModalOpen(false)} searchTerm={searchTerm} />
        </>
    );
};

export default SearchResults;